import React, { useMemo, useRef, useState, useCallback } from "react";
import {
  Card,
  Button,
  Typography,
  Flex,
  Modal,
  Input,
  message,
  Row,
  Col,
  Select,
  Checkbox,
} from "antd";
import { useReactToPrint } from "react-to-print";
import "./SalarySection.scss";
import { format, getMonth, getYear } from "date-fns";
import { useTranslation } from "react-i18next";
import SalaryTable from "../SalaryTable/SalaryTable";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  MailOutlined,
} from "@ant-design/icons";
import PercentageSalaryTable from "../PercentageSalary/PercentageSalary";
import { isAdmin, isSuperAdmin } from "../../../../../../utils/permissions";
import { useAuth } from "../../../../../../utils/useAuth";
import { BASE_API_URL } from "../../../../../../utils/consts";
import axios from "axios";
import OvertimeAccrualModal from "../OvertimeAccrualModal/OvertimeAccrualModal";

const { Text, Title } = Typography;

type Props = {
  openSalaryModal: () => void;
  employeeEarnings: any;
  openBonusPaymentModal: () => void;
  openOvertimePaymentModal: () => void;
  openPercentageSalaryModal: () => void;
  showPercentageSalaryTable: (show: boolean) => void;
  openShortageOverageModal: () => void;
  selectedMonth: any;
  userData: any;
  showDailyWageTable: (show: boolean) => void;
  openTravelExpensesModal: (show: boolean) => void;
  openPenaltyPaymentModal: () => void;
  isTravelExpensesModalOpen: boolean;
  isDetailyWageTableVisible: boolean;
  isPercentageSalaryTableVisible: boolean;
  openLoanPaymentModal: () => void;
  selectedLocationId?: string;
  userWorkLocations?: any[];

  dailyRevenue: any;
  salarySummary?: any;
  salaryHistory?: any[];
  onOvertimeSettingsChanged?: () => void;
};
const SalarySection: React.FC<Props> = ({
  openSalaryModal,
  employeeEarnings,
  openPercentageSalaryModal,
  selectedMonth,
  openBonusPaymentModal,
  openOvertimePaymentModal,
  openPenaltyPaymentModal,
  openShortageOverageModal,
  openLoanPaymentModal,
  userData,
  showDailyWageTable,
  isDetailyWageTableVisible,
  isPercentageSalaryTableVisible,
  showPercentageSalaryTable,
  dailyRevenue,
  isTravelExpensesModalOpen,
  openTravelExpensesModal,
  salarySummary: salarySummaryFromBackend,
  selectedLocationId,
  userWorkLocations,
  salaryHistory = [],
  onOvertimeSettingsChanged,
}) => {
  const { t } = useTranslation();
  const printRef = useRef(null);
  const [total, setTotal] = useState(undefined);
  const [totalPercentageSalary, setTotalPercentageSalary] = useState(undefined);
  const [totalLeaves, setTotalLeaves] = useState(0);
  const authUser: any = useAuth();
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [payoutValue, setPayoutValue] = useState("");
  const [payoutDescription, setPayoutDescription] = useState("");
  const [payoutLocationId, setPayoutLocationId] = useState<string | null>(null);
  const [isSendingSalary, setIsSendingSalary] = useState(false);
  const [includeLoans, setIncludeLoans] = useState(true);
  const [dsoDetailTab, setDsoDetailTab] = useState<
    "dnevnice" | "ispomoc" | "satnica" | "odsustva"
  >("dnevnice");
  const [hoveredPointIdx, setHoveredPointIdx] = useState<number | null>(null);
  const [chartIncludeLoans, setChartIncludeLoans] = useState(false);
  const [isOvertimeAccrualModalOpen, setIsOvertimeAccrualModalOpen] = useState(false);
  //   //Fixed price calculation
  // Sortira po kalendarskom danu (UTC), a za isti dan koristi _id kao tie-breaker.
  // Noviji _id = kasniji unos = važi kao aktivan salary.
  function sortByDateThenId(a: any, b: any): number {
    const dayA = new Date(a.date).toISOString().slice(0, 10);
    const dayB = new Date(b.date).toISOString().slice(0, 10);
    if (dayA < dayB) return -1;
    if (dayA > dayB) return 1;
    return String(a._id).localeCompare(String(b._id));
  }

  function getLocKey(s: any): string {
    if (!s.locationId) return "null";
    if (typeof s.locationId === "object")
      return s.locationId._id?.toString() ?? "null";
    return s.locationId.toString();
  }

  function getFixedSalaryForMonth(
    salaryData: any[],
    year: number,
    month: number,
  ): number {
    if (!salaryData || !salaryData.length) return 0;

    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Grupišemo po locationId — sumiramo sve lokacije kao backend
    const locationMap = new Map<string, any[]>();
    salaryData
      .filter((s) => s && s.date)
      .forEach((s) => {
        const locKey = getLocKey(s);
        if (!locationMap.has(locKey)) locationMap.set(locKey, []);
        locationMap.get(locKey)!.push(s);
      });

    let totalSalary = 0;

    locationMap.forEach((entries) => {
      const sorted = [...entries].sort(sortByDateThenId);

      let locationTotal = 0;
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day, 23, 59, 59);
        const valid = sorted.filter(
          (s) => new Date(s.date).getTime() <= currentDate.getTime(),
        );
        if (valid.length > 0) {
          locationTotal +=
            valid[valid.length - 1].salaryTypes?.fixedSalaries || 0;
        }
      }
      totalSalary += Math.round(locationTotal / daysInMonth);
    });

    return totalSalary;
  }

  function calculateTotalBonusPercentage(data: any) {
    return data.reduce((sum: any, item: any) => sum + (item.total || 0), 0);
  }

  function calculateTotalShortageOverage(data: any[]) {
    if (!data || !Array.isArray(data)) return 0;

    return data.reduce((sum, item) => {
      const val = Number(item.total || 0);
      return item.type === "shortage" ? sum - val : sum + val;
    }, 0);
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Obracun_zarade_${format(
      new Date(selectedMonth),
      "MMMM_yyyy",
    )}`,
    pageStyle: `
    @page {
      size: A4;
      margin: 20mm;
    }
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      font-family: 'Inter', sans-serif;
    }
  `,
  });

  function getTravelExpensesForMonth(
    salaryData: any[],
    year: number,
    month: number,
  ): number {
    if (!salaryData || !salaryData.length) return 0;

    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const locationMap = new Map<string, any[]>();
    salaryData
      .filter((s) => s && s.date)
      .forEach((s) => {
        const locKey = getLocKey(s);
        if (!locationMap.has(locKey)) locationMap.set(locKey, []);
        locationMap.get(locKey)!.push(s);
      });

    let totalExpenses = 0;

    locationMap.forEach((entries) => {
      const sorted = [...entries].sort(sortByDateThenId);

      let locationTotal = 0;
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day, 23, 59, 59);
        const valid = sorted.filter(
          (s) => new Date(s.date).getTime() <= currentDate.getTime(),
        );
        if (valid.length > 0) {
          locationTotal +=
            valid[valid.length - 1].salaryTypes?.travelExpenses || 0;
        }
      }
      totalExpenses += Math.round(locationTotal / daysInMonth);
    });

    return totalExpenses;
  }
  function calculateTotalShifstHours(data: any) {
    if (!data || !Array.isArray(data)) return 0;

    const total = data.reduce(
      (sum: number, item: any) => sum + (item.durationInHours || 0),
      0,
    );

    return Number(total.toFixed(2)); // zaokruženo na 2 decimale
  }

  const canViewValues =
    isSuperAdmin(authUser?.roles) ||
    isAdmin(authUser?.roles) ||
    authUser?.id === userData?.user?._id;

  const handleSubmitPayout = async () => {
    try {
      const month = new Date(selectedMonth).toISOString().slice(0, 7);

      const existingPayout = employeeEarnings?.payoutToBank?.find(
        (p: any) =>
          p.month === month &&
          (!payoutLocationId ||
            !p.locationId ||
            p.locationId === payoutLocationId),
      );

      if (existingPayout) {
        // update
        await axios.put(
          `${BASE_API_URL}/userPayments/${userData?.user?._id}/payoutToBank/${existingPayout._id}`,
          {
            month,
            total: Number(payoutValue),
            description: payoutDescription,
            locationId: payoutLocationId,
          },
        );
        message.success("Isplata ažurirana!");
      } else {
        // create
        await axios.post(
          `${BASE_API_URL}/userPayments/${userData?.user?._id}/payoutToBank`,
          {
            month,
            total: Number(payoutValue),
            description: payoutDescription,
            locationId: payoutLocationId,
          },
        );
        message.success("Isplata uspešno sačuvana!");
      }

      setIsPayoutModalOpen(false);
      setPayoutValue("");
      setPayoutDescription("");
      setPayoutLocationId(null);
      window.location.reload();
    } catch (error) {
      console.error(error);
      message.error("Greška prilikom unosa isplate.");
    }
  };

  /**
   * Izračunava koliko treba odbiti od plate za trenutni mjesec na osnovu pozajmica.
   */
  const calculateFlexibleRepaymentsForMonth = (
    loan: any,
    selectedMonthValue: any,
  ) => {
    if (!loan?.flexibleRepayments?.length) return 0;

    const selectedDate =
      typeof selectedMonthValue?.toDate === "function"
        ? selectedMonthValue.toDate()
        : new Date(selectedMonthValue);
    const targetYear = getYear(selectedDate);
    const targetMonth = getMonth(selectedDate);

    return loan.flexibleRepayments.reduce((sum: number, repayment: any) => {
      if (!repayment?.date) return sum;
      const repaymentDate = new Date(repayment.date);
      if (
        getYear(repaymentDate) === targetYear &&
        getMonth(repaymentDate) === targetMonth
      ) {
        return sum + Number(repayment.amount || 0);
      }
      return sum;
    }, 0);
  };

  const calculateFlexibleOutstanding = (loan: any) => {
    if (loan?.didHeRepayTheDebt) return 0;
    const repaid = (loan?.flexibleRepayments || []).reduce(
      (acc: number, repayment: any) => acc + Number(repayment.amount || 0),
      0,
    );
    return Math.max(0, Number(loan.totalAmount || 0) - repaid);
  };

  const calculateLoanDeduction = useCallback(
    (loans: any[], selectedMonth: any): number => {
      if (!Array.isArray(loans) || loans.length === 0) return 0;

      const currentYear = getYear(new Date(selectedMonth));
      const currentMonth = getMonth(new Date(selectedMonth)); // 0-11

      let totalDeduction = 0;

      for (const loan of loans) {
        const loanStart = new Date(loan.startDate);
        const loanStartYear = getYear(loanStart);
        const loanStartMonth = getMonth(loanStart);

        // Ako je plata unapred (advance_salary)
        if (loan.type === "advance_salary") {
          // Odbija se u istom mesecu kada je pozajmica uzeta
          if (
            currentYear === loanStartYear &&
            currentMonth === loanStartMonth
          ) {
            totalDeduction += loan.totalAmount;
          }
        }

        // Ako je pozajmica na rate (installemnt_loan)
        if (loan.type === "installemnt_loan") {
          // Koliko je prošlo meseci od starta do trenutnog meseca
          const monthsPassed =
            (currentYear - loanStartYear) * 12 +
            (currentMonth - loanStartMonth);

          if (
            monthsPassed >= 0 && // aktivno od početka
            monthsPassed < loan.numberOfIntallments // još traje
          ) {
            totalDeduction += loan.monthlyInstallment || 0;
          }
        }

        if (loan.type === "flexible_payout") {
          totalDeduction += calculateFlexibleRepaymentsForMonth(
            loan,
            selectedMonth,
          );
        }
      }

      return totalDeduction;
    },
    [],
  );

  /**
   * Generiše detalje o pozajmicama za prikaz
   */
  function getLoanDetailsForMonth(loans: any[], selectedMonth: any) {
    if (!Array.isArray(loans) || loans.length === 0) return [];

    const currentYear = getYear(new Date(selectedMonth));
    const currentMonth = getMonth(new Date(selectedMonth));
    const loanDetails: Array<{
      date: string | null;
      label: string;
      description?: string;
      amount: number;
    }> = [];

    for (const loan of loans) {
      const loanStart = new Date(loan.startDate);
      const loanStartYear = getYear(loanStart);
      const loanStartMonth = getMonth(loanStart);

      if (loan.type === "installemnt_loan") {
        const monthsPassed =
          (currentYear - loanStartYear) * 12 + (currentMonth - loanStartMonth);

        if (monthsPassed >= 0 && monthsPassed < loan.numberOfIntallments) {
          loanDetails.push({
            date: loan.startDate,
            label: `${t("Installment")} ${monthsPassed + 1}/${loan.numberOfIntallments}`,
            description: loan.notes,
            amount: loan.monthlyInstallment || 0,
          });
        }
      }

      if (loan.type === "advance_salary") {
        if (currentYear === loanStartYear && currentMonth === loanStartMonth) {
          loanDetails.push({
            date: loan.startDate,
            label: t("Advance salary"),
            description: loan.notes,
            amount: loan.totalAmount || 0,
          });
        }
      }

      if (loan.type === "flexible_payout") {
        for (const repayment of loan.flexibleRepayments || []) {
          if (!repayment?.date) continue;
          const repaymentDate = new Date(repayment.date);
          if (
            getYear(repaymentDate) === currentYear &&
            getMonth(repaymentDate) === currentMonth
          ) {
            loanDetails.push({
              date: repayment.date,
              label: t("Flexible repayment"),
              description: repayment.description || loan.notes,
              amount: Number(repayment.amount || 0),
            });
          }
        }
      }
    }

    return loanDetails;
  }

  const formatCurrencyValue = (val?: number) => {
    const safeValue = Number.isFinite(Number(val)) ? Number(val) : 0;
    return `${safeValue.toLocaleString("sr-RS")} RSD`;
  };

  const renderCurrencyValue = (val?: number) =>
    canViewValues ? formatCurrencyValue(val) : "****";

  const renderSignedCurrencyValue = (val: number = 0) => {
    if (!canViewValues) return "****";
    const absolute = formatCurrencyValue(Math.abs(val));
    return `${val < 0 ? "-" : ""}${absolute}`;
  };

  const salarySummary = useMemo(() => {
    const selectedDateRawBackend =
      typeof selectedMonth?.toDate === "function"
        ? selectedMonth.toDate()
        : new Date(selectedMonth ?? new Date());
    const selectedDateBackend = new Date(selectedDateRawBackend);
    if (Number.isNaN(selectedDateBackend.getTime())) {
      return null;
    }
    const selectedMonthKey = selectedDateBackend.toISOString().slice(0, 7);

    if (
      salarySummaryFromBackend &&
      salarySummaryFromBackend.monthLabel === selectedMonthKey
    ) {
      // koristimo backend izračun ali prilagođavamo za includeLoans
      const backendLoanDeduction = includeLoans
        ? (salarySummaryFromBackend.loanDeduction ?? 0)
        : 0;

      // Ako backend vrati 0 za dnevnice ali frontend ispravno izračuna veću vrijednost
      // (zbog lokacija bez dnevnice koje koriste fallback), koristi frontend vrijednost.
      const frontendDailyMinusLeaves =
        (Number(total) || 0) - (Number(totalLeaves) || 0);
      const effectiveDaily =
        (salarySummaryFromBackend.daily ?? 0) === 0 &&
        frontendDailyMinusLeaves > 0
          ? frontendDailyMinusLeaves
          : (salarySummaryFromBackend.daily ?? 0);
      const effectiveLeavesValue =
        (salarySummaryFromBackend.leavesValue ?? 0) === 0 &&
        (Number(totalLeaves) || 0) > 0
          ? Number(totalLeaves) || 0
          : (salarySummaryFromBackend.leavesValue ?? 0);

      // Preračunaj totale kada su pozajmice isključene
      const backendBruto =
        (salarySummaryFromBackend.fixed ?? 0) +
        effectiveDaily +
        effectiveLeavesValue +
        (salarySummaryFromBackend.percentage ?? 0) +
        (salarySummaryFromBackend.bonus ?? 0) +
        (salarySummaryFromBackend.overtime ?? 0) +
        (salarySummaryFromBackend.autoOvertime ?? 0) +
        (salarySummaryFromBackend.shortageOverage ?? 0) +
        (salarySummaryFromBackend.travel ?? 0) -
        (salarySummaryFromBackend.penalty ?? 0);

      const backendNetForMonth = backendBruto - backendLoanDeduction;
      const backendPayoutAmount = salarySummaryFromBackend.payoutAmount ?? 0;
      const backendOverpayForMonth = Math.max(
        0,
        backendPayoutAmount - backendNetForMonth,
      );
      const backendCashAmount = Math.max(
        0,
        backendNetForMonth - backendPayoutAmount,
      );
      const backendTotalForMonth = backendNetForMonth + backendOverpayForMonth;

      return {
        ...salarySummaryFromBackend,
        daily: effectiveDaily,
        leavesValue: effectiveLeavesValue,
        loanDeduction: backendLoanDeduction,
        overpayForMonth: backendOverpayForMonth,
        cashAmount: backendCashAmount,
        totalForMonth: backendTotalForMonth,
        monthLabel: format(selectedDateBackend, "MMMM yyyy"),
        salaryPayload: {
          hourlyRates: "-",
          dailyWages: formatCurrencyValue(effectiveDaily),
          temporaryHelps: "-",
          percentageRates: formatCurrencyValue(
            salarySummaryFromBackend.percentage ?? 0,
          ),
          travelExpenses: formatCurrencyValue(
            salarySummaryFromBackend.travel ?? 0,
          ),
          fixedSalaries: formatCurrencyValue(
            salarySummaryFromBackend.fixed ?? 0,
          ),
          overtime: formatCurrencyValue(salarySummaryFromBackend.overtime ?? 0),
          bonus: formatCurrencyValue(salarySummaryFromBackend.bonus ?? 0),
          penalty: `-${formatCurrencyValue(
            salarySummaryFromBackend.penalty ?? 0,
          )}`,
          shortageOverage:
            (salarySummaryFromBackend.shortageOverage ?? 0) >= 0
              ? formatCurrencyValue(
                  salarySummaryFromBackend.shortageOverage ?? 0,
                )
              : `-${formatCurrencyValue(
                  Math.abs(salarySummaryFromBackend.shortageOverage ?? 0),
                )}`,
          payoutToTheBank: formatCurrencyValue(backendPayoutAmount),
          loan: `-${formatCurrencyValue(backendLoanDeduction)}`,
          total: formatCurrencyValue(backendTotalForMonth),
        },
      };
    }

    if (!userData?.user?.salary) return null;
    const selectedDateRawLocal =
      typeof selectedMonth?.toDate === "function"
        ? selectedMonth.toDate()
        : new Date(selectedMonth ?? new Date());
    const selectedDate = new Date(selectedDateRawLocal);
    if (Number.isNaN(selectedDate.getTime())) {
      return null;
    }

    const fixed = getFixedSalaryForMonth(
      userData.user.salary,
      getYear(selectedDate),
      getMonth(selectedDate),
    );
    const travel = getTravelExpensesForMonth(
      userData.user.salary,
      getYear(selectedDate),
      getMonth(selectedDate),
    );
    const bonus = calculateTotalBonusPercentage(employeeEarnings?.bonus || []);
    const overtime = calculateTotalBonusPercentage(
      employeeEarnings?.overtime || [],
    );
    const percentage = Number(totalPercentageSalary) || 0;
    const leavesValue = Number(totalLeaves) || 0;
    const totalValue = Number(total) || 0;
    const daily = totalValue - leavesValue;
    const shortage = calculateTotalShortageOverage(
      employeeEarnings?.shortageOverage || [],
    );
    const penalty = calculateTotalBonusPercentage(
      employeeEarnings?.penalty || [],
    );
    const loanDeduction = includeLoans
      ? calculateLoanDeduction(employeeEarnings?.loan || [], selectedMonth)
      : 0;

    const brutoZarada =
      fixed +
      daily +
      leavesValue +
      percentage +
      bonus +
      overtime +
      shortage +
      travel -
      penalty;
    const netForMonth = brutoZarada - loanDeduction;

    const payoutEntry =
      employeeEarnings?.payoutToBank?.find(
        (p: any) => p.month === selectedMonthKey,
      ) ?? null;
    const payoutAmount = payoutEntry?.total || 0;

    const overpayForMonth = Math.max(0, payoutAmount - netForMonth);
    const cashAmount = Math.max(0, netForMonth - payoutAmount);
    const totalForMonth = netForMonth + overpayForMonth; // koliko je stvarno isplaćeno (uključuje preplatu)

    const salaryPayload: Record<string, string> = {
      hourlyRates: "-",
      dailyWages: formatCurrencyValue(daily),
      temporaryHelps: "-",
      percentageRates: formatCurrencyValue(percentage),
      travelExpenses: formatCurrencyValue(travel),
      fixedSalaries: formatCurrencyValue(fixed),
      overtime: formatCurrencyValue(overtime),
      bonus: formatCurrencyValue(bonus),
      penalty: `-${formatCurrencyValue(penalty)}`,
      shortageOverage:
        shortage >= 0
          ? formatCurrencyValue(shortage)
          : `-${formatCurrencyValue(Math.abs(shortage))}`,
      payoutToTheBank: formatCurrencyValue(payoutAmount),
      loan: `-${formatCurrencyValue(loanDeduction)}`,
      total: formatCurrencyValue(totalForMonth),
    };

    return {
      monthLabel: format(selectedDate, "MMMM yyyy"),
      fixed,
      daily,
      leavesValue,
      percentage,
      bonus,
      overtime,
      travel,
      shortageOverage: shortage,
      penalty,
      loanDeduction,
      payoutAmount,
      cashAmount,
      overpayForMonth,
      totalForMonth,
      salaryPayload,
    };
  }, [
    employeeEarnings,
    selectedMonth,
    total,
    totalLeaves,
    totalPercentageSalary,
    userData?.user?.salary,
    salarySummaryFromBackend,
    includeLoans,
    calculateLoanDeduction,
  ]);

  const handleSendSalaryNotification = async () => {
    if (!salarySummary || !userData?.user?._id) {
      message.warning(t("Salary data unavailable"));
      return;
    }
    setIsSendingSalary(true);
    console.log("salarySummary", salarySummary);
    try {
      await axios.post(
        `${BASE_API_URL}/userPayments/${userData.user._id}/sendSalaryNotification`,
        {
          message: {
            month: salarySummary.monthLabel,
            salary: salarySummary.salaryPayload,
          },
        },
      );
      message.success(t("Salary report email sent"));
    } catch (error) {
      console.error(error);
      message.error(t("Salary report email failed"));
    } finally {
      setIsSendingSalary(false);
    }
  };

  // ── Computed values for new design ──────────────────────────────────────────
  const totalDsoItems = [
    ...(employeeEarnings?.shiftsForUser?.dailyWage || []),
    ...(employeeEarnings?.shiftsForUser?.temporaryHelp || []),
    ...(employeeEarnings?.shiftsForUser?.hours || []),
    ...(employeeEarnings?.shiftsForUser?.leaves || []),
  ].length;

  const dailyCount = (employeeEarnings?.shiftsForUser?.dailyWage || []).length;
  const helpCount = (employeeEarnings?.shiftsForUser?.temporaryHelp || [])
    .length;
  const hoursTotal = calculateTotalShifstHours(
    employeeEarnings?.shiftsForUser?.hours,
  );
  const leavesCount = (employeeEarnings?.shiftsForUser?.leaves || []).length;

  const selectedMonthKey2 = (() => {
    try {
      const d =
        typeof selectedMonth?.toDate === "function"
          ? selectedMonth.toDate()
          : new Date(selectedMonth ?? new Date());
      return d.toISOString().slice(0, 7);
    } catch {
      return "";
    }
  })();

  const selectedMonthRange = (() => {
    try {
      const d =
        typeof selectedMonth?.toDate === "function"
          ? selectedMonth.toDate()
          : new Date(selectedMonth ?? new Date());
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      return { start: start.toISOString(), end: end.toISOString(), monthKey };
    } catch {
      return { start: "", end: "", monthKey: "" };
    }
  })();

  const isPayoutRecorded = (employeeEarnings?.payoutToBank || []).some(
    (p: any) => p.month === selectedMonthKey2,
  );

  const payoutHistory = useMemo(() => {
    if (!salaryHistory.length) return [];
    return [...salaryHistory]
      .sort((a: any, b: any) => b.month.localeCompare(a.month))
      .slice(0, 6);
  }, [salaryHistory]);

  const chartEffectiveTotal = useCallback(
    (p: any) =>
      chartIncludeLoans
        ? (p.total || 0)
        : (p.bruto || 0),
    [chartIncludeLoans],
  );

  // 6-month stats for spark card — uses getSalaryHistory API response (one entry per month)
  const sparkStats = useMemo(() => {
    if (!salaryHistory.length)
      return { avg6m: 0, ytd: 0, count: 0, monthsWithData: 0 };
    const currentYear = new Date().getFullYear();
    const withData = salaryHistory.filter(
      (p: any) => p.month && chartEffectiveTotal(p) > 0,
    );
    const avg6m = withData.length
      ? Math.round(
          withData.reduce((s: number, p: any) => s + chartEffectiveTotal(p), 0) /
            withData.length,
        )
      : 0;
    const ytdEntries = salaryHistory.filter((p: any) =>
      p.month?.startsWith(String(currentYear)),
    );
    const ytd = ytdEntries.reduce((s: number, p: any) => s + chartEffectiveTotal(p), 0);
    const ytdMonthCount = ytdEntries.filter(
      (p: any) => chartEffectiveTotal(p) > 0,
    ).length;
    return {
      avg6m,
      ytd,
      count: ytdMonthCount,
      monthsWithData: withData.length,
    };
  }, [salaryHistory, chartEffectiveTotal]);

  // Sparkline SVG path — last 6 months from salaryHistory
  const sparkPath = useMemo(() => {
    const pts = [...salaryHistory]
      .sort((a: any, b: any) => a.month.localeCompare(b.month))
      .slice(-6);
    if (pts.length < 2) return null;
    const totals = pts.map((p: any) => chartEffectiveTotal(p));
    const max = Math.max(...totals, 1);
    const min = Math.min(...totals, 0);
    const range = max - min || 1;
    const W = 300;
    const H = 50;
    const pad = 6;
    const step = W / (pts.length - 1);
    const points = pts.map((p: any, i: number) => {
      const eff = chartEffectiveTotal(p);
      const x = Math.round(i * step);
      const y = Math.round(H - pad - ((eff - min) / range) * (H - pad * 2));
      const prev = i > 0 ? pts[i - 1] : null;
      const prevEff = prev ? chartEffectiveTotal(prev) : null;
      const delta = prevEff !== null ? eff - prevEff : null;
      const deltaPct =
        prevEff && prevEff > 0 ? Math.round((delta! / prevEff) * 100) : null;
      const [yr, mo] = (p.month || "").split("-");
      const label =
        !isNaN(Number(yr)) && !isNaN(Number(mo))
          ? new Date(Number(yr), Number(mo) - 1, 1).toLocaleString("sr-RS", {
              month: "long",
              year: "numeric",
            })
          : p.month;
      return { x, y, total: eff, label, delta, deltaPct };
    });
    const last = points[points.length - 1];
    return {
      d: "M" + points.map((p) => `${p.x},${p.y}`).join(" L"),
      lastX: last.x,
      lastY: last.y,
      points,
    };
  }, [salaryHistory, chartEffectiveTotal]);

  const fmt = (val: number) => Math.round(val || 0).toLocaleString("sr-RS");
  const fmtHide = (val: number) => (canViewValues ? fmt(val) : "••••");
  // Shift durations carry minute-precision decimals (e.g. 9h07m = 9.1166...h)
  // — always display hours rounded to at most 2 decimals.
  const fmtH = (val: number) => Math.round((val || 0) * 100) / 100;

  const earningsTotal = salarySummary
    ? (salarySummary.fixed || 0) +
      (salarySummary.daily || 0) +
      (salarySummary.leavesValue || 0) +
      (salarySummary.percentage || 0) +
      (salarySummary.bonus || 0) +
      (salarySummary.overtime || 0) +
      (salarySummary.autoOvertime || 0) +
      (salarySummary.travel || 0) +
      Math.max(0, salarySummary.shortageOverage || 0)
    : 0;

  const deductionsTotal = salarySummary
    ? (salarySummary.penalty || 0) +
      (salarySummary.loanDeduction || 0) +
      Math.max(0, -(salarySummary.shortageOverage || 0))
    : 0;

  type DeductionDetail = {
    date: string | null;
    label?: string;
    description?: string;
    amount: number;
    isCredit?: boolean;
  };

  const sortByDate = (a: DeductionDetail, b: DeductionDetail) =>
    new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime();

  const penaltyDetails: DeductionDetail[] = (employeeEarnings?.penalty || [])
    .map((item: any) => ({
      date: item.date,
      description: item.description,
      amount: Number(item.total || 0),
    }))
    .sort(sortByDate);

  const shortageDetails: DeductionDetail[] = (
    employeeEarnings?.shortageOverage || []
  )
    .map((item: any) => ({
      date: item.date,
      label: item.type === "shortage" ? t("Shortage") : t("Overage"),
      description: item.description,
      amount: Number(item.total || 0),
      isCredit: item.type !== "shortage",
    }))
    .sort(sortByDate);

  const loanDetails: DeductionDetail[] = getLoanDetailsForMonth(
    employeeEarnings?.loan || [],
    selectedMonth,
  ).sort(sortByDate);

  const renderDeductionDetails = (items: DeductionDetail[]) =>
    items.length > 0 && (
      <div className="ss-rec-details">
        {items.map((item, idx) => (
          <div className="ss-rec-detail" key={idx}>
            <span className="ss-rec-detail-date">
              {item.date ? format(new Date(item.date), "dd.MM.yyyy") : "—"}
            </span>
            <span className="ss-rec-detail-desc">
              {[item.label, item.description].filter(Boolean).join(" · ")}
            </span>
            <span
              className={`ss-rec-detail-val ${item.isCredit ? "pos" : ""}`}
            >
              {canViewValues
                ? `${item.isCredit ? "+" : "−"} ${fmt(item.amount)}`
                : "••••"}
            </span>
          </div>
        ))}
      </div>
    );

  const currentMonthBruto = salarySummary
    ? (salarySummary.fixed || 0) +
      (salarySummary.daily || 0) +
      (salarySummary.leavesValue || 0) +
      (salarySummary.percentage || 0) +
      (salarySummary.bonus || 0) +
      (salarySummary.overtime || 0) +
      (salarySummary.autoOvertime || 0) +
      (salarySummary.travel || 0) +
      (salarySummary.shortageOverage || 0) -
      (salarySummary.penalty || 0)
    : 0;

  const currentMonthEffective = chartIncludeLoans
    ? (salarySummary?.totalForMonth || 0)
    : currentMonthBruto;

  const monthName = salarySummary?.monthLabel?.split(" ")?.[0] || "";

  return (
    <>
      {/* ── SPARK OVERVIEW CARD ─────────────────────────────────────── */}
      <div className="ss-spark-card">
        <div className="ss-spark-stat">
          <span className="ss-spark-lbl">Trenutno za {monthName}</span>
          <span className="ss-spark-val">
            {fmtHide(currentMonthEffective)}
            <span className="ss-spark-cur">RSD</span>
          </span>
          {salarySummary && sparkStats.avg6m > 0 && (
            <span
              className={`ss-spark-delta ${currentMonthEffective >= sparkStats.avg6m ? "up" : "down"}`}
            >
              {currentMonthEffective >= sparkStats.avg6m ? "▲" : "▼"}{" "}
              {Math.abs(
                Math.round(
                  ((currentMonthEffective - sparkStats.avg6m) /
                    (sparkStats.avg6m || 1)) *
                    100,
                ),
              )}
              % u odnosu na prosek
            </span>
          )}
        </div>
        <div className="ss-spark-divider" />
        <div className="ss-spark-stat">
          <span className="ss-spark-lbl">Prosek (12m)</span>
          <span className="ss-spark-val">
            {fmtHide(sparkStats.avg6m)}
            <span className="ss-spark-cur">RSD</span>
          </span>
          <span className="ss-spark-meta">
            {sparkStats.monthsWithData > 0
              ? `${sparkStats.monthsWithData} mesec${sparkStats.monthsWithData === 1 ? "" : sparkStats.monthsWithData < 5 ? "a" : "i"} sa unosima`
              : "Nema isplata"}
          </span>
        </div>
        <div className="ss-spark-divider" />
        <div className="ss-spark-stat">
          <span className="ss-spark-lbl">Isplaćeno YTD</span>
          <span className="ss-spark-val">
            {fmtHide(sparkStats.ytd)}
            <span className="ss-spark-cur">RSD</span>
          </span>
          <span className="ss-spark-meta">
            {sparkStats.count} isplata · tekuća godina
          </span>
        </div>
        <div className="ss-spark-divider" />
        <div className="ss-spark-chart-header">
          <label className="ss-spark-loan-toggle">
            <input
              type="checkbox"
              checked={chartIncludeLoans}
              onChange={(e) => setChartIncludeLoans(e.target.checked)}
            />
            <span>Sa pozajmicama</span>
          </label>
        </div>
        <div
          className="ss-spark-chart-wrap"
          onMouseLeave={() => setHoveredPointIdx(null)}
        >
          {/* Tooltip */}
          {sparkPath &&
            hoveredPointIdx !== null &&
            (() => {
              const pt = sparkPath.points[hoveredPointIdx];
              const isLeft =
                hoveredPointIdx < Math.floor(sparkPath.points.length / 2);
              return (
                <div
                  className="ss-spark-tip"
                  style={{
                    left: `${(pt.x / 300) * 100}%`,
                    transform: isLeft ? "translateX(0)" : "translateX(-100%)",
                  }}
                >
                  <div className="ss-spark-tip-month">{pt.label}</div>
                  <div className="ss-spark-tip-val">
                    {canViewValues
                      ? `${pt.total.toLocaleString("sr-RS")} RSD`
                      : "•••• RSD"}
                  </div>
                  {pt.delta !== null && (
                    <div
                      className={`ss-spark-tip-delta ${pt.delta >= 0 ? "up" : "down"}`}
                    >
                      {pt.delta >= 0 ? "▲" : "▼"}{" "}
                      {Math.abs(pt.delta).toLocaleString("sr-RS")} RSD
                      {pt.deltaPct !== null &&
                        ` (${pt.delta >= 0 ? "+" : ""}${pt.deltaPct}%)`}
                    </div>
                  )}
                </div>
              );
            })()}

          <svg
            className="ss-spark-chart"
            viewBox="0 0 300 50"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="ss-sg" x1="0" x2="0" y1="0" y2="1">
                <stop
                  offset="0"
                  stopColor="oklch(0.55 0.16 265)"
                  stopOpacity="0.22"
                />
                <stop
                  offset="1"
                  stopColor="oklch(0.55 0.16 265)"
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>
            {sparkPath ? (
              <>
                <path
                  d={`${sparkPath.d} L${sparkPath.lastX},50 L0,50 Z`}
                  fill="url(#ss-sg)"
                />
                <path
                  d={sparkPath.d}
                  fill="none"
                  stroke="oklch(0.55 0.16 265)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Hover hit areas — wide invisible rects per segment */}
                {sparkPath.points.map((pt, i) => (
                  <rect
                    key={i}
                    x={
                      i === 0
                        ? 0
                        : sparkPath.points[i - 1].x +
                          (pt.x - sparkPath.points[i - 1].x) / 2
                    }
                    y={0}
                    width={
                      i === 0
                        ? sparkPath.points[1].x / 2
                        : i === sparkPath.points.length - 1
                          ? 300 -
                            (sparkPath.points[i - 1].x +
                              (pt.x - sparkPath.points[i - 1].x) / 2)
                          : (sparkPath.points[i + 1].x -
                              sparkPath.points[i - 1].x) /
                            2
                    }
                    height={50}
                    fill="transparent"
                    style={{ cursor: "crosshair" }}
                    onMouseEnter={() => setHoveredPointIdx(i)}
                  />
                ))}
                {/* Dots */}
                {sparkPath.points.map((pt, i) => (
                  <circle
                    key={i}
                    cx={pt.x}
                    cy={pt.y}
                    r={
                      hoveredPointIdx === i
                        ? 5
                        : i === sparkPath.points.length - 1
                          ? 4
                          : 0
                    }
                    fill={
                      hoveredPointIdx === i
                        ? "oklch(0.55 0.16 265)"
                        : "oklch(0.55 0.16 265)"
                    }
                    stroke="#fff"
                    strokeWidth="2"
                    style={{ transition: "r 0.1s" }}
                    onMouseEnter={() => setHoveredPointIdx(i)}
                  />
                ))}
              </>
            ) : (
              <line
                x1="0"
                y1="25"
                x2="300"
                y2="25"
                stroke="oklch(0.93 0.006 260)"
                strokeWidth="1.5"
                strokeDasharray="4,4"
              />
            )}
          </svg>
        </div>
      </div>

      {/* ── MAIN PAY GRID ───────────────────────────────────────────── */}
      <div className="ss-pay-grid">
        {/* ══ LEFT: Komponente zarade ══════════════════════════════════ */}
        <div className="ss-pay-left">
          <div className="ss-comp-header">
            <div>
              <h3 className="ss-comp-title">Komponente zarade</h3>
              <p className="ss-comp-subtitle">
                {salarySummary?.monthLabel || ""} · {userData?.user?.firstName}{" "}
                {userData?.user?.lastName}
              </p>
            </div>
            <button
              className="ss-btn-ghost"
              onClick={() => openSalaryModal()}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Zarada zaposlenog
            </button>
          </div>

          <div className="ss-comp-grid">
            {/* 1. Fiksna zarada — featured */}
            <div className="ss-comp featured">
              <div className="ss-comp-head">
                <span className="ss-comp-icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <rect x="2" y="6" width="20" height="12" rx="2" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </span>
                <div>
                  <div className="ss-comp-name">{t("Fixed Salary")}</div>
                  <div className="ss-comp-label">Osnovna mesečna zarada</div>
                </div>
                <button
                  className="ss-comp-add"
                  onClick={() => openSalaryModal()}
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M4 20h4L20 8l-4-4L4 16z" />
                  </svg>
                  Uredi
                </button>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.fixed || 0) === 0 ? "zero" : ""}`}
              >
                {fmtHide(salarySummary?.fixed || 0)}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">Osnovna fiksna zarada za mesec</div>
            </div>

            {/* 2. Zarada u procentima */}
            <div
              className={`ss-comp has-detail ${isPercentageSalaryTableVisible ? "open" : ""}`}
            >
              <div className="ss-comp-head">
                <span className="ss-comp-icon violet">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <path d="M3 17l6-6 4 4 8-8" />
                    <path d="M14 7h7v7" />
                  </svg>
                </span>
                <span className="ss-comp-label">{t("Percentage Salary")}</span>
                <div className="ss-comp-actions">
                  {canViewValues && (
                    <button
                      className={`ss-eye-btn ${isPercentageSalaryTableVisible ? "open" : ""}`}
                      onClick={() =>
                        showPercentageSalaryTable(
                          !isPercentageSalaryTableVisible,
                        )
                      }
                    >
                      <svg
                        className="ss-ic-eye"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      <svg
                        className="ss-ic-slash"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <path d="M2 12s3.5-7 10-7c2.5 0 4.7 1 6.5 2.4M22 12s-3.5 7-10 7c-2.5 0-4.7-1-6.5-2.4" />
                        <path d="M3 3l18 18" />
                      </svg>
                    </button>
                  )}
                  <button
                    className="ss-comp-add"
                    onClick={() => openPercentageSalaryModal()}
                  >
                    + Postavi
                  </button>
                </div>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.percentage || 0) === 0 ? "zero" : "pos"}`}
              >
                {canViewValues
                  ? (salarySummary?.percentage || 0) > 0
                    ? `+ ${fmt(salarySummary.percentage)}`
                    : "—"
                  : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">Procenat od pazara/usluga</div>
              <div className="ss-comp-detail">
                <PercentageSalaryTable
                  salaryHistory={(userData?.user?.salary || []).filter(
                    (s: any) => {
                      if (!selectedLocationId) return true;
                      return (
                        s.locationId &&
                        s.locationId.toString() === selectedLocationId
                      );
                    },
                  )}
                  selectedMonth={selectedMonth}
                  dailyRevenue={dailyRevenue}
                  positionInSector={userData?.user?.positionInSector || []}
                  onTotalChange={(amount: any) =>
                    setTotalPercentageSalary(amount)
                  }
                  isDetailyWageTableVisible={isPercentageSalaryTableVisible}
                />
              </div>
            </div>

            {/* 3. Dnevnice / Sati / Odsustva — expandable */}
            <div
              className={`ss-comp has-detail ${isDetailyWageTableVisible ? "open" : ""}`}
            >
              <div className="ss-comp-head">
                <span className="ss-comp-icon ok">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                </span>
                <div>
                  <div className="ss-comp-name">
                    {t("Daily Wage / Hours / Leaves")}
                  </div>
                  <div className="ss-comp-label">
                    Dnevni obračun · {totalDsoItems} stavki
                  </div>
                </div>
                <div className="ss-comp-actions">
                  {canViewValues && (
                    <button
                      className={`ss-eye-btn ${isDetailyWageTableVisible ? "open" : ""}`}
                      onClick={() =>
                        showDailyWageTable(!isDetailyWageTableVisible)
                      }
                    >
                      <svg
                        className="ss-ic-eye"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      <svg
                        className="ss-ic-slash"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <path d="M2 12s3.5-7 10-7c2.5 0 4.7 1 6.5 2.4M22 12s-3.5 7-10 7c-2.5 0-4.7-1-6.5-2.4" />
                        <path d="M3 3l18 18" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div
                className={`ss-comp-value ${(Number(total) || 0) === 0 ? "zero" : "pos"}`}
              >
                {canViewValues ? `+ ${fmt(Number(total) || 0)}` : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">
                <b>{dailyCount}</b> dnevnica · <b>{helpCount}</b> ispomoći ·{" "}
                <b>{hoursTotal}h</b> satnice · <b>{leavesCount}</b> odsustva
              </div>
              <div className="ss-comp-detail">
                <SalaryTable
                  salary={(userData?.user?.salary || []).filter((s: any) => {
                    if (!selectedLocationId) return true;
                    return (
                      s.locationId &&
                      s.locationId.toString() === selectedLocationId
                    );
                  })}
                  shiftsForUser={
                    employeeEarnings?.shiftsForUser || {
                      dailyWage: [],
                      temporaryHelp: [],
                      hours: [],
                    }
                  }
                  selectedMonth={selectedMonth}
                  isDetailyWageTableVisible={isDetailyWageTableVisible}
                  onTotalChange={(amount: any) => {
                    setTotal(amount.total);
                    setTotalLeaves(amount.leaves);
                  }}
                />
              </div>
            </div>

            {/* 4. Bonus */}
            <div className="ss-comp">
              <div className="ss-comp-head">
                <span className="ss-comp-icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <circle cx="12" cy="9" r="5" />
                    <path d="M8.5 13L7 21l5-3 5 3-1.5-8" />
                  </svg>
                </span>
                <span className="ss-comp-label">{t("Bonus")}</span>
                <button
                  className="ss-comp-add"
                  onClick={() => openBonusPaymentModal()}
                >
                  + {t("Add bonus")}
                </button>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.bonus || 0) === 0 ? "zero" : "pos"}`}
              >
                {canViewValues
                  ? (salarySummary?.bonus || 0) > 0
                    ? `+ ${fmt(salarySummary.bonus)}`
                    : "—"
                  : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">
                {(salarySummary?.bonus || 0) > 0
                  ? `${(employeeEarnings?.bonus || []).length} stavki`
                  : "Nema bonusa za ovaj period"}
              </div>
            </div>

            {/* 5. Prekovremeni */}
            <div className="ss-comp">
              <div className="ss-comp-head">
                <span className="ss-comp-icon ok">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v4l2.5 2.5" />
                  </svg>
                </span>
                <span className="ss-comp-label">{t("Overtime")}</span>
                <button
                  className="ss-comp-add"
                  onClick={() => openOvertimePaymentModal()}
                >
                  + {t("Add overtime")}
                </button>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.overtime || 0) === 0 ? "zero" : "pos"}`}
              >
                {canViewValues
                  ? (salarySummary?.overtime || 0) > 0
                    ? `+ ${fmt(salarySummary.overtime)}`
                    : "—"
                  : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">
                {(salarySummary?.overtime || 0) > 0
                  ? `${(employeeEarnings?.overtime || []).length} stavki`
                  : "Nema prekovremenih za ovaj period"}
              </div>
            </div>

            {/* 5b. Auto-prekovremeni (detektovano iz smena) */}
            <div className="ss-comp">
              <div className="ss-comp-head">
                <span className="ss-comp-icon ok">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" />
                  </svg>
                </span>
                <span className="ss-comp-label">Auto-prekovremeni</span>
                <button
                  className="ss-comp-add"
                  onClick={() => setIsOvertimeAccrualModalOpen(true)}
                >
                  Pregled
                </button>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.autoOvertime || 0) === 0 ? "zero" : "pos"}`}
              >
                {canViewValues
                  ? (salarySummary?.autoOvertime || 0) > 0
                    ? `+ ${fmt(salarySummary.autoOvertime)}`
                    : "—"
                  : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">
                {(salarySummary?.autoOvertimeHours || 0) > 0
                  ? `${fmtH(salarySummary.autoOvertimeHours)}h preko planiranog`
                  : "Nema detektovanih prekovremenih sati"}
              </div>
            </div>

            {/* 6. Penali */}
            <div className="ss-comp">
              <div className="ss-comp-head">
                <span className="ss-comp-icon rose">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M9 9l6 6M15 9l-6 6" />
                  </svg>
                </span>
                <span className="ss-comp-label">{t("Penalties")}</span>
                <button
                  className="ss-comp-add"
                  onClick={() => openPenaltyPaymentModal()}
                >
                  + {t("Add penalty")}
                </button>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.penalty || 0) === 0 ? "zero" : "neg"}`}
              >
                {canViewValues
                  ? (salarySummary?.penalty || 0) > 0
                    ? `− ${fmt(salarySummary.penalty)}`
                    : "—"
                  : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">
                {(salarySummary?.penalty || 0) > 0
                  ? `${(employeeEarnings?.penalty || []).length} stavki`
                  : "Nema penala za ovaj period"}
              </div>
            </div>

            {/* 7. Putni troškovi */}
            <div className="ss-comp">
              <div className="ss-comp-head">
                <span className="ss-comp-icon warn">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <circle cx="12" cy="6" r="3" />
                    <path d="M6 21l4-9h4l4 9" />
                  </svg>
                </span>
                <span className="ss-comp-label">{t("Travel Expenses")}</span>
                <button
                  className="ss-comp-add"
                  onClick={() => openTravelExpensesModal(true)}
                >
                  + Dodaj
                </button>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.travel || 0) === 0 ? "zero" : "pos"}`}
              >
                {canViewValues
                  ? (salarySummary?.travel || 0) > 0
                    ? `+ ${fmt(salarySummary.travel)}`
                    : "—"
                  : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">Putni troškovi za mesec</div>
            </div>

            {/* 8. Pozajmica */}
            <div className="ss-comp">
              <div className="ss-comp-head">
                <span className="ss-comp-icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <path d="M3 12l4-4 3 3 5-5 6 6-4 4-3-3-5 5z" />
                  </svg>
                </span>
                <span className="ss-comp-label">{t("Loans")}</span>
                <button
                  className="ss-comp-add"
                  onClick={() => openLoanPaymentModal()}
                >
                  + Dodaj
                </button>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.loanDeduction || 0) === 0 ? "zero" : "neg"}`}
              >
                {canViewValues
                  ? (salarySummary?.loanDeduction || 0) > 0
                    ? `− ${fmt(salarySummary.loanDeduction)}`
                    : "—"
                  : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">
                {(employeeEarnings?.loan?.length || 0) > 0
                  ? `${employeeEarnings.loan.length} aktivnih pozajmica`
                  : "Bez aktivnih pozajmica"}
              </div>
            </div>

            {/* 9. Manjak / Višak */}
            <div className="ss-comp">
              <div className="ss-comp-head">
                <span className="ss-comp-icon warn">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <path d="M3 6h18M3 12h18M3 18h12" />
                  </svg>
                </span>
                <span className="ss-comp-label">{t("Shortage Overage")}</span>
                <button
                  className="ss-comp-add"
                  onClick={() => openShortageOverageModal()}
                >
                  + Unesi
                </button>
              </div>
              <div
                className={`ss-comp-value ${(salarySummary?.shortageOverage || 0) === 0 ? "zero" : (salarySummary?.shortageOverage || 0) > 0 ? "pos" : "neg"}`}
              >
                {canViewValues
                  ? (salarySummary?.shortageOverage || 0) !== 0
                    ? `${(salarySummary.shortageOverage || 0) > 0 ? "+ " : "− "}${fmt(Math.abs(salarySummary.shortageOverage || 0))}`
                    : "—"
                  : "••••"}
                <span className="ss-cur">RSD</span>
              </div>
              <div className="ss-comp-sub">Provera kase</div>
            </div>
          </div>
          {/* /ss-comp-grid */}
        </div>
        {/* /ss-pay-left */}

        {/* ══ RIGHT: Receipt ═══════════════════════════════════════════ */}
        <div className="ss-receipt">
          <div className="ss-receipt-head">
            <span className="ss-receipt-ic">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              >
                <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2z" />
                <path d="M8 8h8M8 12h8M8 16h5" />
              </svg>
            </span>
            <div style={{ flex: 1 }}>
              <h3>Obračun zarade — {salarySummary?.monthLabel || ""}</h3>
              <div className="ss-receipt-sub">
                {userData?.user?.firstName} {userData?.user?.lastName}
              </div>
            </div>
            <span className={`ss-pay-status ${isPayoutRecorded ? "paid" : ""}`}>
              <span className="ss-status-dot" />
              {isPayoutRecorded ? "Isplaćeno" : "U izradi"}
            </span>
          </div>

          {salarySummary ? (
            <div ref={printRef}>
              <div className="ss-print-only-header">
                <h2>{userData?.user?.firstName} {userData?.user?.lastName}</h2>
                <p>Obračun zarade — {salarySummary?.monthLabel || ""}</p>
              </div>
              {/* Section: Zarada */}
              <div className="ss-receipt-section">
                <div className="ss-receipt-section-title">
                  <span>Zarada</span>
                  <span className="ss-mono" style={{ color: "var(--ss-ok)" }}>
                    {canViewValues ? `+${fmt(earningsTotal)} RSD` : "••••"}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Fiksna zarada</span>
                  <span
                    className={`ss-val ${(salarySummary.fixed || 0) === 0 ? "zero" : ""}`}
                  >
                    {fmtHide(salarySummary.fixed || 0)}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Dnevnice</span>
                  <span
                    className={`ss-val ${(salarySummary.daily || 0) === 0 ? "zero" : ""}`}
                  >
                    {fmtHide(salarySummary.daily || 0)}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Odsustva</span>
                  <span
                    className={`ss-val ${(salarySummary.leavesValue || 0) === 0 ? "zero" : ""}`}
                  >
                    {fmtHide(salarySummary.leavesValue || 0)}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Procenat</span>
                  <span
                    className={`ss-val ${(salarySummary.percentage || 0) === 0 ? "zero" : ""}`}
                  >
                    {fmtHide(salarySummary.percentage || 0)}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Bonus</span>
                  <span
                    className={`ss-val ${(salarySummary.bonus || 0) === 0 ? "zero" : "pos"}`}
                  >
                    {canViewValues
                      ? (salarySummary.bonus || 0) > 0
                        ? `+ ${fmt(salarySummary.bonus)}`
                        : "0"
                      : "••••"}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Prekovremeni</span>
                  <span
                    className={`ss-val ${(salarySummary.overtime || 0) === 0 ? "zero" : "pos"}`}
                  >
                    {canViewValues
                      ? (salarySummary.overtime || 0) > 0
                        ? `+ ${fmt(salarySummary.overtime)}`
                        : "0"
                      : "••••"}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">
                    Auto-prekovremeni{" "}
                    {(salarySummary.autoOvertimeHours || 0) > 0 &&
                      `(${fmtH(salarySummary.autoOvertimeHours)}h)`}
                  </span>
                  <span
                    className={`ss-val ${(salarySummary.autoOvertime || 0) === 0 ? "zero" : "pos"}`}
                  >
                    {canViewValues
                      ? (salarySummary.autoOvertime || 0) > 0
                        ? `+ ${fmt(salarySummary.autoOvertime)}`
                        : "0"
                      : "••••"}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Putni troškovi</span>
                  <span
                    className={`ss-val ${(salarySummary.travel || 0) === 0 ? "zero" : "pos"}`}
                  >
                    {canViewValues
                      ? (salarySummary.travel || 0) > 0
                        ? `+ ${fmt(salarySummary.travel)}`
                        : "0"
                      : "••••"}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Manjak / Višak</span>
                  <span
                    className={`ss-val ${(salarySummary.shortageOverage || 0) === 0 ? "zero" : (salarySummary.shortageOverage || 0) > 0 ? "pos" : "neg"}`}
                  >
                    {canViewValues
                      ? renderSignedCurrencyValue(
                          salarySummary.shortageOverage || 0,
                        ).replace(" RSD", "")
                      : "••••"}
                  </span>
                </div>
              </div>

              {/* Section: Odbici */}
              <div className="ss-receipt-section">
                <div className="ss-receipt-section-title">
                  <span>Odbici od plate</span>
                  <span className="ss-mono" style={{ color: "var(--ss-rose)" }}>
                    {canViewValues && deductionsTotal > 0
                      ? `−${fmt(deductionsTotal)} RSD`
                      : "—"}
                  </span>
                </div>
                <div className="ss-check-row ss-no-print">
                  <input
                    type="checkbox"
                    id="ss-ded-loan"
                    checked={includeLoans}
                    onChange={(e) => setIncludeLoans(e.target.checked)}
                  />
                  <label htmlFor="ss-ded-loan">{t("Include loans")}</label>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Penali</span>
                  <span
                    className={`ss-val ${(salarySummary.penalty || 0) === 0 ? "zero" : "neg"}`}
                  >
                    {canViewValues
                      ? (salarySummary.penalty || 0) > 0
                        ? `− ${fmt(salarySummary.penalty)}`
                        : "0"
                      : "••••"}
                  </span>
                </div>
                {renderDeductionDetails(penaltyDetails)}
                {includeLoans && (
                  <div className="ss-rec-row">
                    <span className="ss-lbl">Pozajmice</span>
                    <span
                      className={`ss-val ${(salarySummary.loanDeduction || 0) === 0 ? "zero" : "neg"}`}
                    >
                      {canViewValues
                        ? (salarySummary.loanDeduction || 0) > 0
                          ? `− ${fmt(salarySummary.loanDeduction)}`
                          : "0"
                        : "••••"}
                    </span>
                  </div>
                )}
                {includeLoans && renderDeductionDetails(loanDetails)}
                {(salarySummary.shortageOverage || 0) < 0 && (
                  <>
                    <div className="ss-rec-row">
                      <span className="ss-lbl">{t("Shortage Overage")}</span>
                      <span className="ss-val neg">
                        {canViewValues
                          ? `− ${fmt(-(salarySummary.shortageOverage || 0))}`
                          : "••••"}
                      </span>
                    </div>
                    {renderDeductionDetails(shortageDetails)}
                  </>
                )}
                {salarySummary.overpayForMonth > 0 && (
                  <div className="ss-rec-row">
                    <span className="ss-lbl">Minus (preplata)</span>
                    <span className="ss-val neg">
                      {canViewValues
                        ? `− ${fmt(salarySummary.overpayForMonth)}`
                        : "••••"}
                    </span>
                  </div>
                )}
              </div>

              {/* Section: Isplata */}
              <div className="ss-receipt-section">
                <div className="ss-receipt-section-title">
                  <span>Isplata</span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">Na bankovni račun</span>
                  <span
                    className={`ss-val ${(salarySummary.payoutAmount || 0) === 0 ? "zero" : ""}`}
                  >
                    {fmtHide(salarySummary.payoutAmount || 0)}
                  </span>
                </div>
                <div className="ss-rec-row">
                  <span className="ss-lbl">U gotovini</span>
                  <span
                    className={`ss-val ${(salarySummary.cashAmount || 0) === 0 ? "zero" : ""}`}
                  >
                    {fmtHide(salarySummary.cashAmount || 0)}
                  </span>
                </div>
                {salarySummary.overpayForMonth > 0 && (
                  <div className="ss-rec-row">
                    <span className="ss-lbl">{t("Overpaid by")}</span>
                    <span className="ss-val neg">
                      {fmtHide(salarySummary.overpayForMonth)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="ss-receipt-empty">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <span>Nema podataka za ovaj period</span>
            </div>
          )}

          <div className="ss-rec-total">
            <div>
              <div className="ss-rec-total-label">Ukupno za {monthName}</div>
              <div className="ss-rec-total-sub">
                net · {isPayoutRecorded ? "isplaćeno" : "pripremljeno"}
              </div>
            </div>
            <div className="ss-rec-total-amount">
              {fmtHide(salarySummary?.totalForMonth || 0)}
              <span className="ss-cur">RSD</span>
            </div>
          </div>

          <div className="ss-receipt-actions">
            <button
              className="ss-btn-ghost ss-receipt-act-btn"
              onClick={handlePrint}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              >
                <path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5h20v5a2 2 0 0 1-2 2h-2M6 14h12v7H6z" />
              </svg>
              Štampaj
            </button>
            <button
              className="ss-btn-ghost ss-receipt-act-btn"
              onClick={() => {
                const existingPayout = employeeEarnings?.payoutToBank?.find(
                  (p: any) =>
                    p.month === selectedMonthKey2 &&
                    (!selectedLocationId ||
                      !p.locationId ||
                      p.locationId === selectedLocationId),
                );
                if (existingPayout) {
                  setPayoutValue(existingPayout.total);
                  setPayoutDescription(existingPayout.description || "");
                  setPayoutLocationId(existingPayout.locationId || null);
                } else {
                  setPayoutValue("");
                  setPayoutDescription("");
                  setPayoutLocationId(selectedLocationId || null);
                }
                setIsPayoutModalOpen(true);
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              >
                <path d="M12 4v12M6 12l6 6 6-6M4 20h16" />
              </svg>
              {isPayoutRecorded ? "Uredi isplatu" : "Unesi isplatu"}
            </button>
            {canViewValues && (
              <button
                className="ss-btn-primary ss-receipt-act-btn"
                onClick={handleSendSalaryNotification}
                disabled={isSendingSalary}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
                </svg>
                {isSendingSalary ? "Šalje..." : "Pošalji obračun"}
              </button>
            )}
          </div>
        </div>
        {/* /ss-receipt */}
      </div>
      {/* /ss-pay-grid */}

      {/* ── ISTORIJA PLATA ──────────────────────────────────────────── */}
      {payoutHistory.length > 0 && (
        <div className="ss-pay-history">
          <div className="ss-card-head">
            <span className="ss-card-ic">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </span>
            <div className="ss-card-titles">
              <h3>Istorija plata</h3>
              <div>Isplate za poslednjih 6 meseci (račun + gotovina)</div>
            </div>
          </div>
          <table className="ss-pay-table">
            <thead>
              <tr>
                <th>Mesec</th>
                <th>Isplata</th>
                <th>Status</th>
                <th>Ukupno</th>
              </tr>
            </thead>
            <tbody>
              {payoutHistory.map((payout: any) => {
                const [y, m] = (payout.month || "").split("-");
                const monthDate = new Date(Number(y), Number(m) - 1, 1);
                const mLabel = !isNaN(monthDate.getTime())
                  ? format(monthDate, "MMMM yyyy")
                  : payout.month;
                const isPaid =
                  (payout.payoutToBank || 0) + (payout.cashAmount || 0) > 0;
                return (
                  <tr key={payout.month}>
                    <td>
                      <div className="ss-month-cell">{mLabel}</div>
                    </td>
                    <td>
                      <span className="ss-meta">
                        {(payout.payoutToBank || 0) > 0 &&
                          canViewValues &&
                          `Na račun: ${payout.payoutToBank?.toLocaleString("sr-RS")} RSD`}
                        {(payout.payoutToBank || 0) > 0 &&
                          (payout.cashAmount || 0) > 0 &&
                          " · "}
                        {(payout.cashAmount || 0) > 0 &&
                          canViewValues &&
                          `Gotovina: ${payout.cashAmount?.toLocaleString("sr-RS")} RSD`}
                        {!canViewValues && "••••"}
                        {canViewValues &&
                          (payout.payoutToBank || 0) === 0 &&
                          (payout.cashAmount || 0) === 0 &&
                          "—"}
                      </span>
                    </td>
                    <td>
                      <span className={`ss-pay-status ${isPaid ? "paid" : ""}`}>
                        <span className="ss-status-dot" />
                        {isPaid ? "Isplaćeno" : "Obračunato"}
                      </span>
                    </td>
                    <td className="ss-amount-cell">
                      {canViewValues
                        ? `${chartEffectiveTotal(payout).toLocaleString("sr-RS")} RSD`
                        : "••••"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── PAYOUT MODAL ────────────────────────────────────────────── */}
      <Modal
        title="Isplata na račun"
        open={isPayoutModalOpen}
        className="standardHeader"
        onCancel={() => setIsPayoutModalOpen(false)}
        footer={null}
      >
        <Flex vertical gap={10}>
          <div>
            <Text>Iznos (RSD)</Text>
            <Input
              type="number"
              value={payoutValue}
              onChange={(e) => setPayoutValue(e.target.value)}
              placeholder="Unesi iznos"
            />
          </div>
          <div>
            <Text>Opis</Text>
            <Input
              value={payoutDescription}
              onChange={(e) => setPayoutDescription(e.target.value)}
              placeholder="Opis isplate (opciono)"
            />
          </div>
          {userWorkLocations && userWorkLocations.length > 1 && (
            <div>
              <Text>Lokacija</Text>
              <Select
                style={{ width: "100%" }}
                placeholder="Izaberi lokaciju"
                value={payoutLocationId}
                onChange={(value) => setPayoutLocationId(value)}
                allowClear
              >
                {userWorkLocations.map((loc: any) => (
                  <Select.Option key={loc._id} value={loc._id}>
                    {loc.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}
        </Flex>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            gap: 10,
            marginTop: 20,
          }}
        >
          <Button onClick={() => setIsPayoutModalOpen(false)}>Otkaži</Button>
          <Button type="primary" onClick={handleSubmitPayout}>
            Sačuvaj
          </Button>
        </div>
      </Modal>

      {userData?.user?._id && (
        <OvertimeAccrualModal
          isOpen={isOvertimeAccrualModalOpen}
          onClose={() => setIsOvertimeAccrualModalOpen(false)}
          userId={userData.user._id}
          monthStart={selectedMonthRange.start}
          monthEnd={selectedMonthRange.end}
          viewedMonthKey={selectedMonthRange.monthKey}
          onSaved={onOvertimeSettingsChanged}
        />
      )}
    </>
  );
};

export default SalarySection;
